import { Flex, IconButton, Tooltip } from '@radix-ui/themes'
import { Link, useLocation } from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { useTranslation } from 'react-i18next'
import {
	RxChevronLeft,
	RxChevronRight,
	RxDoubleArrowLeft,
	RxDoubleArrowRight,
} from 'react-icons/rx'

import { type HydraView } from '~/models/ecommerce-providers/sylius/interface'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	uiPager = 'ui-pager',
}

/* -------------------------------------------------------------------------------------------------
 * Pager
 * -----------------------------------------------------------------------------------------------*/

type PagerProps = {
	hydra?: HydraView | undefined
	path?: string
	total?: number
	itemsPerPage?: number
	page?: number
	className?: string
	preventScrollReset?: boolean
}

const Pager: React.FC<PagerProps> = (props) => {
	const {
		hydra,
		path,
		total,
		itemsPerPage,
		page,
		className,
		preventScrollReset = false,
	} = props

	const { pathname, search } = useLocation()
	const { t } = useTranslation(I18NEXT.uiPager)

	if (total && itemsPerPage && page) {
		if (total <= itemsPerPage) {
			return null
		}

		const to = path || pathname
		const params = new URLSearchParams(search)
		const current = page
		const last = Math.ceil(total / itemsPerPage)

		const list = []
		for (let i = -2, page = current + i; i <= 2; i++, page = current + i) {
			if (page <= 0) {
				continue
			}

			params.set('page', page.toString())

			list.push({
				uri: params.toString(),
				page: page,
			})

			if (list[list.length - 1].page === last) {
				break
			}
		}

		return (
			<Flex
				justify="center"
				align="center"
				gapX="2"
				className={cn('text-gray-11', className)}
			>
				{current !== 1 && (
					<Tooltip content={t('firstPage-$val', { val: 1 })}>
						<Link
							preventScrollReset={preventScrollReset}
							to={(() => {
								params.set('page', '1')

								return `${to}?${params.toString()}`
							})()}
							prefetch="viewport"
						>
							<IconButton variant="soft" radius="full">
								<RxDoubleArrowLeft />
							</IconButton>
						</Link>
					</Tooltip>
				)}

				{current !== 1 && (
					<Tooltip content={t('previousPage-$val', { val: current - 1 })}>
						<Link
							preventScrollReset={preventScrollReset}
							to={(() => {
								params.set('page', (current - 1).toString())

								return `${to}?${params.toString()}`
							})()}
							prefetch="viewport"
						>
							<IconButton variant="soft" radius="full">
								<RxChevronLeft />
							</IconButton>
						</Link>
					</Tooltip>
				)}

				{list.map(({ uri, page }) => (
					<Tooltip key={page} content={t('page-$val', { val: page })}>
						<Link
							preventScrollReset={preventScrollReset}
							to={`${to}?${uri}`}
							prefetch="viewport"
						>
							<IconButton
								variant="soft"
								radius="full"
								disabled={page === current}
							>
								{page}
							</IconButton>
						</Link>
					</Tooltip>
				))}

				{current !== last && (
					<Tooltip content={t('nextPage-$val', { val: current + 1 })}>
						<Link
							preventScrollReset={preventScrollReset}
							to={(() => {
								params.set('page', (current + 1).toString())

								return `${to}?${params.toString()}`
							})()}
							prefetch="viewport"
						>
							<IconButton variant="soft" radius="full">
								<RxChevronRight />
							</IconButton>
						</Link>
					</Tooltip>
				)}

				{current !== last && (
					<Tooltip content={t('lastPage-$val', { val: last })}>
						<Link
							preventScrollReset={preventScrollReset}
							to={(() => {
								params.set('page', last.toString())

								return `${to}?${params.toString()}`
							})()}
							prefetch="viewport"
						>
							<IconButton variant="soft" radius="full">
								<RxDoubleArrowRight />
							</IconButton>
						</Link>
					</Tooltip>
				)}
			</Flex>
		)
	}

	if (!hydra?.hasOwnProperty('hydra:first')) {
		return null
	}

	const to = path || pathname
	const params = new URL(hydra['@id']!, 'http://a').searchParams
	const current = Number.parseInt(params.get('page') || '1')

	const list = []
	for (let i = -2, page = current + i; i <= 2; i++, page = current + i) {
		if (page <= 0) {
			continue
		}

		params.set('page', page.toString())

		list.push({
			uri: params.toString(),
			page: page,
		})

		if (
			list[list.length - 1].uri ===
			new URL(hydra['hydra:last']!, 'http://a').searchParams.toString()
		) {
			break
		}
	}

	return (
		<Flex
			justify="center"
			align="center"
			gapX="2"
			className={cn('text-gray-11', className)}
		>
			{hydra.hasOwnProperty('hydra:first') && current !== 1 && (
				<Tooltip
					content={t('firstPage-$val', {
						val: new URL(hydra['hydra:first']!, 'http://a').searchParams.get(
							'page',
						),
					})}
				>
					<Link
						preventScrollReset={preventScrollReset}
						to={`${to}?${new URL(hydra['hydra:first']!, 'http://a').searchParams}`}
						prefetch="viewport"
					>
						<IconButton variant="soft" radius="full">
							<RxDoubleArrowLeft />
						</IconButton>
					</Link>
				</Tooltip>
			)}

			{hydra.hasOwnProperty('hydra:previous') && (
				<Tooltip
					content={t('previousPage-$val', {
						val: new URL(hydra['hydra:previous']!, 'http://a').searchParams.get(
							'page',
						),
					})}
				>
					<Link
						preventScrollReset={preventScrollReset}
						to={`${to}?${new URL(hydra['hydra:previous']!, 'http://a').searchParams}`}
						prefetch="viewport"
					>
						<IconButton variant="soft" radius="full">
							<RxChevronLeft />
						</IconButton>
					</Link>
				</Tooltip>
			)}

			{list.map(({ uri, page }) => (
				<Tooltip
					key={page}
					content={t('page-$val', {
						val: new URLSearchParams(uri).get('page'),
					})}
				>
					<Link
						preventScrollReset={preventScrollReset}
						to={`${to}?${uri}`}
						prefetch="viewport"
					>
						<IconButton
							variant="soft"
							radius="full"
							disabled={page === current}
						>
							{page}
						</IconButton>
					</Link>
				</Tooltip>
			))}

			{hydra.hasOwnProperty('hydra:next') && (
				<Tooltip
					content={t('nextPage-$val', {
						val: new URL(hydra['hydra:next']!, 'http://a').searchParams.get(
							'page',
						),
					})}
				>
					<Link
						preventScrollReset={preventScrollReset}
						to={`${to}?${new URL(hydra['hydra:next']!, 'http://a').searchParams}`}
						prefetch="viewport"
					>
						<IconButton variant="soft" radius="full">
							<RxChevronRight />
						</IconButton>
					</Link>
				</Tooltip>
			)}

			{hydra.hasOwnProperty('hydra:last') &&
				list[list.length - 1].page !== current && (
					<Tooltip
						content={t('lastPage-$val', {
							val: new URL(hydra['hydra:last']!, 'http://a').searchParams.get(
								'page',
							),
						})}
					>
						<Link
							preventScrollReset={preventScrollReset}
							to={`${to}?${new URL(hydra['hydra:last']!, 'http://a').searchParams}`}
							prefetch="viewport"
						>
							<IconButton variant="soft" radius="full">
								<RxDoubleArrowRight />
							</IconButton>
						</Link>
					</Tooltip>
				)}
		</Flex>
	)
}
Pager.displayName = 'Pager'

/* -----------------------------------------------------------------------------------------------*/

export { Pager, I18NEXT as PAGER_I18NEXT }
